import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Globe, MapPin, User, FileText, Info, ClipboardList } from 'lucide-react'
import { Toggle } from '@anyemp/ui-kit'
import { users, talents } from '../../services/apiService'
import toast from 'react-hot-toast'
import ManagerSelect from '../UI/ManagerSelect'
import UserSelect from '../UI/UserSelect'
import Avatar from '../UI/Avatar'
import SectionBlock from '../UI/SectionBlock'
import SectionHeader from '../UI/SectionHeader'

const formatDate = (dateString) => {
  if (!dateString) return 'Not specified'
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
    })
  } catch {
    return 'Invalid date'
  }
}

const InfoCard = ({ children, colSpan2 }) => (
  <div className={`min-w-0 border border-light-border-primary dark:border-dark-border-primary rounded-lg p-3 bg-light-bg-tertiary dark:bg-dark-bg-tertiary${colSpan2 ? ' sm:col-span-2' : ''}`}>
    {children}
  </div>
)

const OverviewSection = ({ talent, talentId, hasPermission, setTalent }) => {
  const [userOptions, setUserOptions] = useState([])
  const [linkedUserOptions, setLinkedUserOptions] = useState([])
  const [isEditingManager, setIsEditingManager] = useState(false)
  const [managerChangeLoading, setManagerChangeLoading] = useState(false)
  const [isEditingLinkedUser, setIsEditingLinkedUser] = useState(false)
  const [linkedUserChangeLoading, setLinkedUserChangeLoading] = useState(false)
  const [isActiveLoading, setIsActiveLoading] = useState(false)

  const [usersLoaded, setUsersLoaded] = useState(false)
  const [linkedUsersLoaded, setLinkedUsersLoaded] = useState(false)
  const [usersOptionsLoading, setUsersOptionsLoading] = useState(false)
  const [linkedUsersOptionsLoading, setLinkedUsersOptionsLoading] = useState(false)

  const loadUserOptions = () => {
    if (usersLoaded) return
    setUsersLoaded(true)
    setUsersOptionsLoading(true)
    users.getAllUsers()
      .then(res => {
        if (res?.success) setUserOptions(Array.isArray(res.data) ? res.data : res.data?.users || [])
      })
      .catch(err => console.error('Error loading users:', err))
      .finally(() => setUsersOptionsLoading(false))
  }

  const loadLinkedUserOptions = () => {
    if (linkedUsersLoaded || !talentId) return
    setLinkedUsersLoaded(true)
    setLinkedUsersOptionsLoading(true)
    talents.getUsersNotInTalents(talentId)
      .then(res => {
        const list = res?.data || res
        setLinkedUserOptions(Array.isArray(list) ? list : list?.users || [])
      })
      .catch(err => console.error('Error loading linked user options:', err))
      .finally(() => setLinkedUsersOptionsLoading(false))
  }

  const handleManagerChange = async (managerId) => {
    const newManagerId = managerId === '' ? null : managerId
    try {
      setManagerChangeLoading(true)
      await talents.updateTalentManager(talentId, newManagerId)
      const newManager = newManagerId ? userOptions.find(u => u.id === newManagerId) || null : null
      setTalent(prev => ({ ...prev, manager_id: newManagerId, manager: newManager }))
      setIsEditingManager(false)
      toast.success('Manager updated', { duration: 2000, position: 'bottom-right' })
    } catch (err) {
      console.error('Error updating manager:', err)
      toast.error('Failed to update manager', { duration: 2000, position: 'bottom-right' })
    } finally {
      setManagerChangeLoading(false)
    }
  }

  const handleIsActiveToggle = async (e) => {
    const val = e.target.checked
    setIsActiveLoading(true)
    try {
      await talents.updateTalentActivity(talentId, val)
      setTalent(prev => ({ ...prev, is_active: val }))
      toast.success(val ? 'Talent activated' : 'Talent deactivated', { duration: 2000, position: 'bottom-right' })
    } catch (err) {
      console.error('Error updating is_active:', err)
      toast.error('Failed to update activity status', { duration: 2000, position: 'bottom-right' })
    } finally {
      setIsActiveLoading(false)
    }
  }

  const handleLinkedUserChange = async (userId) => {
    const newUserId = userId === '' ? null : userId
    try {
      setLinkedUserChangeLoading(true)
      await talents.updateTalentLinkedUser(talentId, newUserId)
      const newUser = newUserId ? userOptions.find(u => u.id === newUserId) || null : null
      setTalent(prev => ({ ...prev, auth_user_id: newUserId, authUser: newUser }))
      setIsEditingLinkedUser(false)
      toast.success('Linked user updated', { duration: 2000, position: 'bottom-right' })
    } catch (err) {
      console.error('Error updating linked user:', err)
      toast.error('Failed to update linked user', { duration: 2000, position: 'bottom-right' })
    } finally {
      setLinkedUserChangeLoading(false)
    }
  }

  return (
    <SectionBlock id="overview">
      <SectionHeader icon={Info} title="Basic Info" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <InfoCard>
          <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Last Name</p>
          <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">{talent.last_name || '—'}</p>
        </InfoCard>

        <InfoCard>
          <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1">First Name</p>
          <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">{talent.first_name || '—'}</p>
        </InfoCard>

        {talent.country?.name && (
          <InfoCard>
            <div className="flex items-center gap-1.5 mb-1">
              <Globe className="h-3.5 w-3.5 text-light-text-tertiary dark:text-dark-text-tertiary" />
              <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Country</p>
            </div>
            <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">{talent.country.name}</p>
          </InfoCard>
        )}

        {talent.city?.name && (
          <InfoCard>
            <div className="flex items-center gap-1.5 mb-1">
              <MapPin className="h-3.5 w-3.5 text-light-text-tertiary dark:text-dark-text-tertiary" />
              <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">City</p>
            </div>
            <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">{talent.city.name}</p>
          </InfoCard>
        )}

        {talent.birth_year && (
          <InfoCard>
            <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Age</p>
            <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
              {new Date().getFullYear() - talent.birth_year} ({talent.birth_year})
            </p>
          </InfoCard>
        )}

        {/* Заявка, из которой создан талант. Обратная ссылка — в шапке JobApplicationDetail */}
        {talent.job_application_id && (
          <InfoCard>
            <div className="flex items-center gap-1.5 mb-1">
              <ClipboardList className="h-3.5 w-3.5 text-light-text-tertiary dark:text-dark-text-tertiary" />
              <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Job Application</p>
            </div>
            <Link
              to={`/job-applications/${talent.job_application_id}`}
              className="text-sm font-medium text-primary-600 dark:text-primary-400 visited:text-purple-700 dark:visited:text-purple-400 hover:underline"
              title={`Open job application #${talent.job_application_id}`}
            >
              {talent.jobApplication?.name || `#${talent.job_application_id}`}
            </Link>
          </InfoCard>
        )}

        {hasPermission('talent-service', talent.talent_type, 'update') && (
          <InfoCard>
            <div className="flex items-center gap-1.5 mb-1.5">
              <User className="h-3.5 w-3.5 text-light-text-tertiary dark:text-dark-text-tertiary" />
              <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Linked User</p>
            </div>
            <div onMouseEnter={loadLinkedUserOptions} onFocus={loadLinkedUserOptions}>
              <UserSelect
                currentUser={talent.authUser || (talent.auth_user_id ? linkedUserOptions.find(u => u.id === talent.auth_user_id) : null)}
                currentUserId={talent.auth_user_id}
                userOptions={linkedUserOptions}
                isEditing={isEditingLinkedUser}
                isLoading={linkedUserChangeLoading || linkedUsersOptionsLoading}
                onEdit={() => { loadLinkedUserOptions(); setIsEditingLinkedUser(true) }}
                onCancel={() => setIsEditingLinkedUser(false)}
                onChange={handleLinkedUserChange}
              />
            </div>
          </InfoCard>
        )}

        {hasPermission('talent-service', talent.talent_type, 'update') && (
          <InfoCard>
            <div className="flex items-center gap-1.5 mb-1.5">
              <User className="h-3.5 w-3.5 text-light-text-tertiary dark:text-dark-text-tertiary" />
              <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Manager</p>
            </div>
            <div onMouseEnter={loadUserOptions} onFocus={loadUserOptions}>
              <ManagerSelect
                currentManager={talent.manager || (talent.manager_id ? userOptions.find(u => u.id === talent.manager_id) : null)}
                currentManagerId={talent.manager_id}
                managerOptions={userOptions}
                isEditing={isEditingManager}
                isLoading={managerChangeLoading || usersOptionsLoading}
                onEdit={() => { loadUserOptions(); setIsEditingManager(true) }}
                onCancel={() => setIsEditingManager(false)}
                onChange={handleManagerChange}
                emptyLabel="No manager"
              />
            </div>
          </InfoCard>
        )}

        <InfoCard>
          <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Created</p>
          <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-1.5">{formatDate(talent.created_at)}</p>
          {talent.createdByUser && (
            <div className="flex items-center gap-1.5">
              <Avatar avatar={talent.createdByUser.avatar} name={talent.createdByUser.fullName || talent.createdByUser.name} email={talent.createdByUser.email} size="xs" />
              <span className="text-xs text-light-text-secondary dark:text-dark-text-secondary">{talent.createdByUser.fullName || talent.createdByUser.name || talent.createdByUser.email}</span>
            </div>
          )}
        </InfoCard>

        <InfoCard>
          <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Updated</p>
          <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-1.5">{formatDate(talent.updated_at)}</p>
          {talent.updatedByUser && (
            <div className="flex items-center gap-1.5">
              <Avatar avatar={talent.updatedByUser.avatar} name={talent.updatedByUser.fullName || talent.updatedByUser.name} email={talent.updatedByUser.email} size="xs" />
              <span className="text-xs text-light-text-secondary dark:text-dark-text-secondary">{talent.updatedByUser.fullName || talent.updatedByUser.name || talent.updatedByUser.email}</span>
            </div>
          )}
        </InfoCard>

        {talent.status_updated_at && (
          <InfoCard>
            <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Status Updated</p>
            <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">{formatDate(talent.status_updated_at)}</p>
          </InfoCard>
        )}

        {talent.is_active !== undefined && hasPermission('talent-service', talent.talent_type, 'update') && (
          <InfoCard>
            <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Activity</p>
            <div className="flex items-center gap-2">
              <Toggle
                checked={!!talent.is_active}
                isDisabled={isActiveLoading}
                onChange={(checked, e) => handleIsActiveToggle(e)}
                color="success"
                label={talent.is_active ? 'Active' : 'Inactive'}
              />
              {isActiveLoading && <div className="animate-spin h-3.5 w-3.5 border-2 border-green-500 border-t-transparent rounded-full" />}
            </div>
          </InfoCard>
        )}

        {talent.export_note && (
          <InfoCard colSpan2>
            <div className="flex items-center gap-1.5 mb-2">
              <FileText className="h-3.5 w-3.5 text-light-text-tertiary dark:text-dark-text-tertiary" />
              <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Export Note</p>
            </div>
            <pre className="text-sm text-light-text-primary dark:text-dark-text-primary whitespace-pre-wrap font-mono">{talent.export_note}</pre>
          </InfoCard>
        )}
      </div>
    </SectionBlock>
  )
}

export default OverviewSection
